import { formatActivityTimestamp } from './groupActivityByDate.js';
import { formatTraitDate } from './traitDates.js';

import {
  ArrowDownLeft,
  ArrowUpRight,
  CalendarClock,
  Clock,
  Eye,
  FileText,
  ListChecks,
  Mail,
  MessageSquare,
  Phone,
  StickyNote,
  User,
  Users,
  Wrench,
} from 'lucide-react';

/**
 * What an Activity / History card shows for each KIND of item (CT-5527, CT-5528,
 * CT-5724), as `EntityList` slots. One pure builder per kind and one map: a new
 * kind is one entry here, never another branch in the card.
 *
 * Every builder takes `(item, { t, language, onPreview })`; `onPreview` is
 * already bound to the item by the card, and is absent when the page cannot
 * open a preview.
 */

/** The channel a card belongs to; an unknown kind degrades to a generic icon. Tasks match the Tasks tab. */
const KIND_ICON = { email: Mail, sms: MessageSquare, note: StickyNote, call: Phone, meeting: Users, task: ListChecks };
const DIRECTION_ICON = { inbound: ArrowDownLeft, outbound: ArrowUpRight };

/** Inbound reads as light blue, outbound as the brand blue — the direction at a glance. */
const DIRECTION_HIGHLIGHT = {
  inbound: { bgColor: 'LightBlue', iconColor: 'LightBlue' },
  outbound: { bgColor: 'Primary', iconColor: 'Primary' },
};

/** Scope is the one metadatum a note has; private reads as a warning, public as plain information. */
const SCOPE_VARIANT = { public: 'informative', private: 'warning' };

/** "6m 18s" — the length of a call or a meeting. */
const formatDuration = (seconds) => {
  if (seconds == null) return null;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
};

const timestampOf = (item, language) => (item.occurredAt ? formatActivityTimestamp(item.occurredAt, language) : undefined);

/**
 * A NOTE (CT-5528): the scope rides the status badge, the author sits in the
 * footer, the short summary is the AI line, and the eye opens the preview.
 */
export const buildNoteView = (item, { t, language, onPreview }) => ({
  title: item.title ?? undefined,
  icon: StickyNote,
  iconHighlight: { bgColor: 'Yellow', iconColor: 'Yellow' },
  status: item.scope ? { label: t(`notes.scope.${item.scope}`), variant: SCOPE_VARIANT[item.scope] } : undefined,
  timestamp: timestampOf(item, language),
  description: item.preview ?? undefined,
  // A note's summary is short; the AI line is the design's slot for it.
  aiInsight: item.aiSummary ? { label: t('notes.card.summary'), text: item.aiSummary } : undefined,
  secondaryMeta: item.author?.name ? [{ icon: User, label: `${t('notes.card.by')}: ${item.author.name}` }] : [],
  tags: [{ label: t('notes.card.tag') }],
  secondaryCta: onPreview ? { icon: Eye, label: t('notes.card.preview'), onClick: onPreview } : undefined,
});

/** A TASK on History (CT-5724): status, assignee, due date, context, where it came from. */
export const buildTaskView = (item, { t, language }) => {
  const due = formatTraitDate(item.dueAt, language);
  return {
    title: item.title ?? undefined,
    icon: KIND_ICON.task,
    iconHighlight: { bgColor: 'Purple', iconColor: 'Purple' },
    status: item.status?.label ? item.status : undefined,
    timestamp: timestampOf(item, language),
    secondaryMeta: [
      item.assignee ? { icon: User, label: item.assignee } : null,
      due ? { icon: CalendarClock, label: t('activity.card.due', { date: due }) } : null,
      item.context ? { label: item.context } : null,
      item.origin ? { label: item.origin } : null,
    ].filter(Boolean),
    tags: [{ label: t('activity.card.channel.task') }],
  };
};

/**
 * A CALL or a MEETING (CT-5724): a call record (direction, record type, AI
 * summary, phone, duration, agent tools, tone) or one logged by hand.
 */
export const buildInteractionView = (item, { t, language }) => {
  const duration = formatDuration(item.durationSeconds);
  return {
    title: item.title ?? undefined,
    icon: KIND_ICON[item.kind],
    iconHighlight: DIRECTION_HIGHLIGHT[item.direction] ?? DIRECTION_HIGHLIGHT.outbound,
    primaryMeta: [
      item.direction ? { icon: DIRECTION_ICON[item.direction], label: t(`activity.card.${item.direction}`) } : null,
      item.recordLabel ? { label: item.recordLabel } : null,
      item.loggedByMe ? { label: t('activity.card.loggedByYou') } : null,
    ].filter(Boolean),
    status: item.status?.label ? item.status : undefined,
    timestamp: timestampOf(item, language),
    description: item.preview ?? undefined,
    aiInsight: item.aiSummary ? { label: t('activity.card.aiSummary'), text: item.aiSummary } : undefined,
    secondaryMeta: [
      item.phone ? { icon: Phone, label: item.phone } : null,
      duration ? { icon: Clock, label: duration } : null,
      item.agentTools ? { icon: Wrench, label: t('activity.card.agentTools', { count: item.agentTools }) } : null,
      item.participants ? { icon: Users, label: item.participants } : null,
    ].filter(Boolean),
    tags: [item.tone ? { label: item.tone } : { label: t(`activity.card.channel.${item.kind}`) }],
  };
};

/**
 * An EMAIL or an SMS (CT-5527) — and the fallback for a kind this app does not
 * know yet, drawn plainly. No row action in v2.0, so no eye.
 */
export const buildMessageView = (item, { t, language }) => {
  const directionLabel = item.direction ? t(`activity.card.${item.direction}`) : null;
  const secondaryMeta = [];
  if (item.sender) secondaryMeta.push({ icon: User, label: `${t('activity.card.from')}: ${item.sender}` });
  if (item.recipients?.length) {
    secondaryMeta.push({ icon: Users, label: `${t('activity.card.to')}: ${item.recipients.join(', ')}` });
  }

  return {
    title: item.title ?? undefined,
    icon: KIND_ICON[item.kind] ?? FileText,
    iconHighlight: DIRECTION_HIGHLIGHT[item.direction] ?? DIRECTION_HIGHLIGHT.outbound,
    primaryMeta: directionLabel ? [{ icon: DIRECTION_ICON[item.direction], label: directionLabel }] : undefined,
    status: item.status?.label ? item.status : undefined,
    timestamp: timestampOf(item, language),
    description: item.preview ?? undefined,
    aiInsight: item.aiSummary ? { label: t('activity.card.aiSummary'), text: item.aiSummary } : undefined,
    secondaryMeta,
    tags: KIND_ICON[item.kind] ? [{ label: t(`activity.card.channel.${item.kind}`) }] : undefined,
  };
};

/** The builder of each kind; anything else is drawn as a message. */
export const ACTIVITY_VIEW_BY_KIND = Object.freeze({
  note: buildNoteView,
  task: buildTaskView,
  call: buildInteractionView,
  meeting: buildInteractionView,
});

/** The card's view of `item`: its kind's builder, or the message view. */
export const buildActivityCardView = (item, context) =>
  (ACTIVITY_VIEW_BY_KIND[item.kind] ?? buildMessageView)(item, context);
